import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';

import { PATHS } from '../../../constants/paths';
import CardWithProgressLoading from '../CardWithLoadingProgress';
import DialogDetailsPayment from '../DialogDetailsPayment';
import DialogConfirmation from '../DialogConfirmation';
import { toAbsoluteUrl } from '../../../utils/AssetsHelpers';
import {
  useDialogToggleWithData,
  useToggleDialogConfirmation,
  useLazyRequest,
} from '../../hooks';
import { cancelPayment } from '../../../services/merchantsService';

import PaymentsListItem from './Item';
import styles from './styles';

const useStyles = makeStyles(styles);

const PaymentsList = ({ loading, data, reloadData }) => {
  const { t } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const [
    openDialogPaymentDetail,
    selectedPaymentDetail,
    handleOpenDialogPaymentDetail,
    handleCloseDialogPaymentDetail,
  ] = useDialogToggleWithData(null);

  const [
    openConfirmationCancelPayment,
    selectedCancelPaymentId,
    handleOpenConfirmCancelPayment,
    handleCloseConfirmCancelPayment,
  ] = useToggleDialogConfirmation(null);

  const [, loadingCancelPayment, , requestCancelPayment] = useLazyRequest({
    request: cancelPayment,
    withPostSuccess: () => {
      enqueueSnackbar(t('payments.cancelPayment.successMessage'), {
        variant: 'success',
      });

      reloadData();

      handleCloseConfirmCancelPayment();
    },
    withPostFailure: (response) => {
      enqueueSnackbar(response.errorData.message, { variant: 'error' });
    },
  });

  const handleDeletePayment = useCallback(async () => {
    if (selectedCancelPaymentId) {
      await requestCancelPayment(selectedCancelPaymentId);
    }
  }, [requestCancelPayment, selectedCancelPaymentId]);

  return (
    <Box className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        {t('payments.latestPayments')}
      </Typography>

      <CardWithProgressLoading
        loading={loading}
        className={classes.card}
        progressSize={65}
        fullHeight={false}
      >
        {data.length === 0 && (
          <Box className={classes.emptyContainer}>
            <Box
              component="img"
              src={toAbsoluteUrl('/assets/icons/emptyState2.svg')}
              alt="Empty State Icon"
              className={classes.emptyIcon}
            />

            <Typography className={classes.emptyText}>
              {t('payments.noResults')}
            </Typography>
          </Box>
        )}

        {data.length > 0 && (
          <>
            <Box className={classes.list}>
              {data.map((item) => (
                <PaymentsListItem
                  key={`payment-${item.id}`}
                  paymentId={item.id}
                  createdAt={item.created_at}
                  amount={item.total_amount}
                  currency={item.fiat}
                  state={item.summary_state}
                  description={item.kind}
                  invoiceUrl={item.invoice_url}
                  actionsMenu={{
                    onShowDetail: () => handleOpenDialogPaymentDetail(item),
                    onCancel: (paymentId) =>
                      handleOpenConfirmCancelPayment(paymentId),
                  }}
                />
              ))}
            </Box>

            <RouterLink
              to={PATHS.payments.path}
              className={classes.showMoreLink}
            >
              {t('general.viewMore')}
              <span>►</span>
            </RouterLink>
          </>
        )}
      </CardWithProgressLoading>

      {openDialogPaymentDetail && selectedPaymentDetail && (
        <DialogDetailsPayment
          open={openDialogPaymentDetail}
          onClose={() => handleCloseDialogPaymentDetail()}
          data={selectedPaymentDetail}
        />
      )}

      <DialogConfirmation
        open={openConfirmationCancelPayment}
        description={t('payments.cancelPayment.confirmMessage')}
        onConfirm={handleDeletePayment}
        onCancel={() => handleCloseConfirmCancelPayment()}
        loadingConfirm={loadingCancelPayment}
      />
    </Box>
  );
};

PaymentsList.propTypes = {
  loading: PropTypes.bool.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      created_at: PropTypes.string,
      crypto_currency: PropTypes.string,
      fiat: PropTypes.string,
      id: PropTypes.number,
      invoice_url: PropTypes.string,
      kind: PropTypes.string,
      movement_amount: PropTypes.string,
      payer: PropTypes.string,
      quotation: PropTypes.string,
      source_account_identifier: PropTypes.string,
      summary_state: PropTypes.string,
      token: PropTypes.string,
      total_amount: PropTypes.number,
      tributary_identifier_payer: PropTypes.string,
    }),
  ).isRequired,
  reloadData: PropTypes.func.isRequired,
};

export default PaymentsList;
